import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import { AssignLinkRole } from "../../redux/actions/AssignLinkRoleAction";

const AddLinkRoleModal = ({ roles, links, open, onClose }) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState({ idRole: "", idLink: "" });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const isFormValid = () => form.idRole !== "" && form.idLink !== "";

  const handleClose = () => {
    onClose();
    setForm({ idRole: "", idLink: "" });
    setMessage({ type: "", text: "" });
  };

  const handleSave = async () => {
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const result = await dispatch(AssignLinkRole(form.idRole, form.idLink));

      if (result.success) {
        setMessage({
          type: "success",
          text:
            typeof result.message === "string"
              ? result.message
              : "Link assigned to role successfully",
        });
        setForm({ idRole: "", idLink: "" });
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        setMessage({
          type: "error",
          text:
            typeof result.message === "string"
              ? result.message
              : "There was an error assigning the link",
        });
      }
    } catch {
      setMessage({
        type: "error",
        text: "There was an error assigning the link. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign Link to Role</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <TextField
            select
            label="Role"
            value={form.idRole}
            onChange={(e) => setForm({ ...form, idRole: e.target.value })}
            fullWidth
            required
            disabled={loading}
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiInputBase-root": { height: "42px" },
              "& .MuiInputLabel-root": { fontSize: "1.2rem" },
            }}
          >
            {roles.map((role) => (
              <MenuItem key={role.idRole} value={role.idRole}>
                {role.roleName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Link"
            value={form.idLink}
            onChange={(e) => setForm({ ...form, idLink: e.target.value })}
            fullWidth
            required
            disabled={loading}
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiInputBase-root": { height: "42px" },
              "& .MuiInputLabel-root": { fontSize: "1.2rem" },
            }}
          >
            {links.map((link) => (
              <MenuItem key={link.idLink} value={link.idLink}>
                {link.linkName}
              </MenuItem>
            ))}
          </TextField>

          {message.text && (
            <Alert
              severity={message.type}
              onClose={() => setMessage({ type: "", text: "" })}
            >
              {message.text}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isFormValid() || loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{
            backgroundColor: "#4b4bfa",
            "&:hover": { backgroundColor: "#3e3ed9" },
            color: "#fff"
          }}
        >
          {loading ? "Saving..." : "Assign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddLinkRoleModal;
